// storageClassifier.js
// Deterministic storage classifier for the grocery "Conservación" (storage) tab.
//
// Single source of truth for WHERE a purchased product should be stored
// (fridge / freezer / pantry) and HOW LONG it stays usable (post-purchase days).
//
// Design (Variant A — deterministic, NO runtime LLM):
//   The meal-plan prompt already generates a validated `form` field per ingredient
//   ("fresh" | "frozen" | "canned" | "jarred" | "dried" | "cured" | "prepared" |
//   "dairy" | "other"). `form` is the most reliable storage signal and is used here
//   as the PRIMARY input, backed by frozen-keyword detection, fish detection,
//   curated name overrides, PRODUCT_METADATA (read-only) and a category fallback.
//
// This module is PURE (no React) and self-contained: it owns all of its data sets
// (room-temp produce, fridge-after-open condiments, pantry staples, shelf-life tiers)
// so it does NOT depend on the separate PRODUCT_METADATA audit and never mutates it.

import { normalizeIngredientName, findProductMetadata } from './groceryUtils';
import { isFishProductItem, FISH_FRESHNESS_DAYS, FISH_FREEZER_THRESHOLD_DAYS, getRawMeatFridgeCap } from './recipeFreshnessRules';

export const STORAGE = Object.freeze({
  FRIDGE: 'fridge',
  FREEZER: 'freezer',
  PANTRY: 'pantry',
});

// Set of the three valid storage locations, for trusting a caller-provided value.
const VALID_STORAGE = new Set([STORAGE.FRIDGE, STORAGE.FREEZER, STORAGE.PANTRY]);

// Shelf-life (in days) above which the UI relabels the badge as "long-lasting"
// instead of showing a raw number (e.g. canned/dried/frozen staples).
export const LONG_SHELF_LIFE_THRESHOLD = 30;

// Days used for fridge perishables when PRODUCT_METADATA has no entry.
const FRESH_FRIDGE_METADATA_CAP = 14; // clamp any bad metadata value for fridge items

// Frozen detection across the languages we localise product names into.
const FROZEN_KEYWORDS =
  /(frozen|congelad|congelat|congel[eé]|surgel|заморож|морожен|заморож|замороз)/i;

// Fresh produce conventionally kept at room temperature (counter / pantry),
// NOT in the fridge. Kept intentionally tight to avoid false positives.
const ROOM_TEMP_PRODUCE = [
  'onion', 'red onion', 'white onion', 'yellow onion', 'sweet onion', 'brown onion', 'shallot',
  'garlic',
  'potato', 'sweet potato', 'yam',
  'banana', 'plantain',
  'tomato',
  'pumpkin', 'squash', 'butternut squash', 'winter squash', 'acorn squash',
  'pineapple', 'melon', 'watermelon', 'cantaloupe',
  'avocado',
];

// Fresh-produce items that must STAY in the fridge even though their name ends
// with a room-temp keyword (e.g. "spring onion" ends with "onion").
const FRIDGE_PRODUCE_OVERRIDES = [
  'spring onion', 'green onion', 'scallion', 'spring onions', 'green onions',
  'cherry tomato', 'cherry tomatoes',
];

// Condiments/spreads people keep in the fridge (after opening) even though the
// sealed package could sit in a pantry. `form` for these is usually jarred/other.
const FRIDGE_AFTER_OPEN = [
  'mayonnaise', 'mayo', 'mustard', 'ketchup', 'pesto', 'hummus', 'guacamole',
  'fresh salsa', 'tzatziki', 'creme fraiche', 'sour cream',
];

// Unambiguous shelf-stable staples that belong in the pantry regardless of form
// (covers the "other" form, which is ambiguous). Each entry is matched as a word
// so e.g. "black pepper" -> pantry but "bell pepper" (not listed) stays fridge.
const PANTRY_STAPLES = [
  'olive oil', 'vegetable oil', 'sunflower oil', 'oil',
  'vinegar', 'balsamic vinegar', 'balsamic', 'apple cider vinegar',
  'honey', 'sugar', 'brown sugar', 'icing sugar', 'syrup', 'maple syrup',
  'salt', 'sea salt',
  'flour', 'cornflour', 'cornstarch', 'baking powder', 'baking soda', 'yeast', 'cocoa',
  'rice', 'pasta', 'spaghetti', 'macaroni', 'noodle', 'couscous', 'quinoa', 'oat', 'oats',
  'cereal', 'cracker', 'breadcrumb', 'breadcrumbs',
  'coffee', 'tea',
  'black pepper', 'white pepper', 'peppercorn', 'paprika', 'cumin', 'oregano',
  'cinnamon', 'nutmeg', 'turmeric', 'curry powder', 'chili powder', 'cayenne', 'spice',
  'stock cube', 'bouillon', 'soy sauce', 'fish sauce', 'peanut butter',
];

const BAKERY_KEYWORDS = [
  'bread', 'baguette', 'loaf', 'bun', 'roll', 'tortilla', 'pita', 'wrap', 'naan',
  'croissant', 'bagel', 'brioche', 'ciabatta', 'crumpet', 'muffin',
];

// Shelf-life tier table (post-purchase usable days). PRODUCT_METADATA refines
// these when a product is found; otherwise these defaults guarantee a sane value.
const SHELF_LIFE_TIERS = {
  fridge: { fresh: 5, dairy: 7, cured: 12, prepared: 4, default: 5 },
  freezer: { default: 90 },
  pantry: { canned: 365, jarred: 180, dried: 365, prepared: 5, default: 180 },
};

const lc = (value) => String(value || '').toLowerCase().trim();

// Match a normalized (space-separated) name against a keyword list using
// word-boundary-ish matching to avoid substring false positives.
const matchesName = (text, list) =>
  list.some(
    (k) =>
      text === k ||
      text.startsWith(`${k} `) ||
      text.endsWith(` ${k}`) ||
      text.includes(` ${k} `)
  );

// normalizeIngredientName collapses some compound names with a hyphen
// (e.g. "balsamic vinegar" -> "balsamic-vinegar"); de-hyphenate for matching.
const toMatchText = (item) => {
  const raw = item?.name || item?.originalName || item?.productName || '';
  return normalizeIngredientName(raw).replace(/-/g, ' ').trim();
};

const isBakery = (text, category) =>
  matchesName(text, BAKERY_KEYWORDS) || lc(category).includes('bakery');

const storageFromCategory = (category) => {
  const c = lc(category);
  if (c.includes('frozen')) return STORAGE.FREEZER;
  if (
    c.includes('pantry') ||
    c.includes('grain') ||
    c.includes('pasta') ||
    c.includes('condiment') ||
    c.includes('bakery') ||
    c.includes('spice')
  ) {
    return STORAGE.PANTRY;
  }
  // Produce, Meat & Seafood, Dairy, Refrigerated, Other -> fridge (safe default)
  return STORAGE.FRIDGE;
};

const resolveShelfLife = (storage, form, item) => {
  // Preserved forms have a known long shelf life regardless of the fresh-keyed
  // PRODUCT_METADATA (which reflects the fresh version of the ingredient).
  if (form === 'canned' || form === 'dried') return SHELF_LIFE_TIERS.pantry.canned;
  if (form === 'jarred') return SHELF_LIFE_TIERS.pantry.jarred;
  // Frozen items keep for months; metadata (keyed by the fresh ingredient) is wrong here.
  if (storage === STORAGE.FREEZER) return SHELF_LIFE_TIERS.freezer.default;

  // Otherwise prefer curated PRODUCT_METADATA (accurate for fresh / room-temp
  // produce). Clamp fridge perishables so a wrong large value can't show "365 días".
  const meta =
    findProductMetadata(item?.name) ||
    findProductMetadata(item?.originalName) ||
    findProductMetadata(item?.productName);
  const metaDays = Number(meta?.shelfLifeDays);
  let days;
  if (Number.isFinite(metaDays) && metaDays > 0) {
    days = storage === STORAGE.FRIDGE ? Math.min(metaDays, FRESH_FRIDGE_METADATA_CAP) : metaDays;
  } else {
    const tier = SHELF_LIFE_TIERS[storage] || SHELF_LIFE_TIERS.fridge;
    days = tier[form] || tier.default;
  }

  // Food-safety cap for raw meat/poultry kept in the fridge: ground meat &
  // poultry last ~2 days, raw red-meat cuts ~3 days. Guarantees the grocery card
  // and the recipe "fresh for N days" agree and never overstate raw-meat life.
  if (storage === STORAGE.FRIDGE) {
    const meatCap =
      getRawMeatFridgeCap(item?.name) ??
      getRawMeatFridgeCap(item?.originalName) ??
      getRawMeatFridgeCap(item?.productName);
    if (meatCap) days = Math.min(days, meatCap);
  }

  return days;
};

/**
 * Classify a grocery item into a storage location + shelf-life.
 *
 * @param {Object} item - grocery item ({ name, originalName, productName, productHint,
 *                         form, category, ... }). English `name`/`originalName` preferred
 *                         for keyword matching; `productHint`/`productName` may be localised.
 * @param {Object} [options]
 * @param {string} [options.recipeStorageHint] - storage hint from recipe details LLM
 *                         (e.g. 'freezer' for a fish batch). Only honoured for fish.
 * @param {number} [options.requiredFreshnessDays] - how many days the DISH(es) this
 *                         ingredient belongs to must stay fresh. Drives perishables
 *                         (fish, raw meat) into the FREEZER when their fridge life is
 *                         shorter than the dish requires (dish freshness drives storage).
 * @param {string} [options.knownStorage] - a storage location already known to be
 *                         correct (e.g. from previously saved data or recipe
 *                         normalisation). When valid, it is trusted as-is and only a
 *                         consistent shelf life is (re)derived, so the location and
 *                         the day count can never contradict each other.
 * @returns {{ storage: string, shelfLifeDays: number, longLasting: boolean }}
 */
export function classifyItemStorage(item, options = {}) {
  const recipeStorageHint = lc(options.recipeStorageHint);
  const requiredFreshnessDays = Number(options.requiredFreshnessDays);
  const hasFreshnessReq = Number.isFinite(requiredFreshnessDays) && requiredFreshnessDays > 0;
  const form = lc(item?.form);
  const category = item?.category || '';
  const text = toMatchText(item);
  const haystack = `${item?.productHint || ''} ${item?.productName || ''} ${item?.name || ''} ${item?.originalName || ''}`;

  // Food-safety fridge cap for raw meat/poultry (null for cured / fish / non-meat).
  // Computed up front so the dish-driven freezer branch below can use it.
  const meatFridgeCap =
    getRawMeatFridgeCap(item?.name) ??
    getRawMeatFridgeCap(item?.originalName) ??
    getRawMeatFridgeCap(item?.productName);

  // 0. Trust a caller-provided KNOWN storage location. Only re-derive the shelf
  // life so a valid location is never paired with a contradictory day count
  // (e.g. "freezer · 2 days"). Fish kept in the fridge stays hard-capped at 2 days.
  const knownStorage = lc(options.knownStorage);
  if (VALID_STORAGE.has(knownStorage)) {
    if (isFishProductItem(item) && knownStorage === STORAGE.FRIDGE) {
      return { storage: STORAGE.FRIDGE, shelfLifeDays: FISH_FRESHNESS_DAYS, longLasting: false };
    }
    const days = resolveShelfLife(knownStorage, form, item);
    return { storage: knownStorage, shelfLifeDays: days, longLasting: days > LONG_SHELF_LIFE_THRESHOLD };
  }

  let storage;

  // 1. Frozen — explicit form or a frozen keyword anywhere in the (possibly localised) names.
  if (form === 'frozen' || FROZEN_KEYWORDS.test(haystack)) {
    storage = STORAGE.FREEZER;
  }
  // 2. Fish / seafood — fridge 2 days, or freezer when the recipe stores a multi-day
  //    batch OR the dish it belongs to must stay fresh longer than fridge fish allows
  //    (fresh fish keeps ~2 days in the fridge; a 5-day dish requires the freezer).
  //    Preserved forms (canned/jarred/dried, e.g. canned tuna) are shelf-stable and
  //    fall through to the pantry mapping below.
  else if (
    isFishProductItem(item) &&
    form !== 'canned' && form !== 'jarred' && form !== 'dried'
  ) {
    const dishNeedsFreezer = hasFreshnessReq && requiredFreshnessDays > FISH_FREEZER_THRESHOLD_DAYS;
    storage = (recipeStorageHint === 'freezer' || dishNeedsFreezer) ? STORAGE.FREEZER : STORAGE.FRIDGE;
    const days = storage === STORAGE.FREEZER ? SHELF_LIFE_TIERS.freezer.default : FISH_FRESHNESS_DAYS;
    return { storage, shelfLifeDays: days, longLasting: days > LONG_SHELF_LIFE_THRESHOLD };
  }
  // 2b. Raw meat / poultry — kept in the fridge with a food-safety cap, but moved to the
  //     FREEZER when the dish it belongs to must stay fresh longer than the meat keeps
  //     (e.g. tender pork/beef ~3 days, ground meat & poultry ~2 days, in a 5-day dish).
  //     Preserved forms (canned/jarred/dried/cured) are shelf-stable and skipped.
  else if (
    meatFridgeCap &&
    form !== 'canned' && form !== 'jarred' && form !== 'dried' && form !== 'cured' &&
    hasFreshnessReq && requiredFreshnessDays > meatFridgeCap
  ) {
    storage = STORAGE.FREEZER;
    const days = SHELF_LIFE_TIERS.freezer.default;
    return { storage, shelfLifeDays: days, longLasting: days > LONG_SHELF_LIFE_THRESHOLD };
  }
  // 3. Curated name overrides (highest-confidence product knowledge).
  else if (matchesName(text, FRIDGE_PRODUCE_OVERRIDES)) {
    storage = STORAGE.FRIDGE;
  } else if (matchesName(text, ROOM_TEMP_PRODUCE)) {
    storage = STORAGE.PANTRY;
  } else if (matchesName(text, FRIDGE_AFTER_OPEN)) {
    storage = STORAGE.FRIDGE;
  } else if (matchesName(text, PANTRY_STAPLES)) {
    storage = STORAGE.PANTRY;
  }
  // 4. Form-based mapping.
  else if (form === 'canned' || form === 'jarred' || form === 'dried') {
    storage = STORAGE.PANTRY;
  } else if (form === 'dairy' || form === 'cured') {
    storage = STORAGE.FRIDGE;
  } else if (form === 'fresh') {
    storage = STORAGE.FRIDGE;
  } else if (form === 'prepared') {
    storage = isBakery(text, category) ? STORAGE.PANTRY : STORAGE.FRIDGE;
  }
  // 5. Fallback for "other"/unknown form: metadata -> category default.
  else {
    const meta =
      findProductMetadata(item?.name) ||
      findProductMetadata(item?.originalName) ||
      findProductMetadata(item?.productName);
    storage = meta?.storage || storageFromCategory(category);
  }

  const shelfLifeDays = resolveShelfLife(storage, form, item);
  return { storage, shelfLifeDays, longLasting: shelfLifeDays > LONG_SHELF_LIFE_THRESHOLD };
}

/**
 * Build a lookup map of ingredient name -> { form, productHint } from the raw
 * meal plan, BEFORE consolidation drops those fields. Keyed by both the
 * normalized name and the raw lowercase product string. Frozen wins on collision.
 *
 * @param {Array} mealPlan - array of recipe objects with `ingredients[]`.
 * @returns {Map<string, { form: string|null, productHint: string|null }>}
 */
export function buildIngredientFormMap(mealPlan) {
  const map = new Map();
  if (!Array.isArray(mealPlan)) return map;

  const put = (rawName, form, productHint) => {
    if (!rawName) return;
    const keys = [normalizeIngredientName(rawName), lc(rawName)];
    for (const key of keys) {
      if (!key) continue;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { form: form || null, productHint: productHint || null });
      } else {
        if (form === 'frozen') existing.form = 'frozen';
        else if (!existing.form && form) existing.form = form;
        if (!existing.productHint && productHint) existing.productHint = productHint;
      }
    }
  };

  for (const recipe of mealPlan) {
    if (!recipe || !Array.isArray(recipe.ingredients)) continue;
    for (const ing of recipe.ingredients) {
      if (!ing) continue;
      put(ing.product, lc(ing.form), ing.productHint);
    }
  }
  return map;
}

/**
 * Resolve { form, productHint } for a grocery item from a form map.
 * @param {Map} formMap - from buildIngredientFormMap.
 * @param {Object} item - grocery item.
 * @returns {{ form?: string|null, productHint?: string|null }}
 */
export function resolveIngredientForm(formMap, item) {
  if (!formMap || !item) return {};
  return (
    formMap.get(item.name) ||
    formMap.get(lc(item.name)) ||
    formMap.get(normalizeIngredientName(item.originalName)) ||
    formMap.get(lc(item.originalName)) ||
    formMap.get(normalizeIngredientName(item.productName)) ||
    {}
  );
}
